import { useEffect, useMemo, useRef, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  IndustryBenchmarkProfileResponse,
  MetricComparisonKind,
  QuickBooksGrowthAnalysisResponse,
  RatioAnalysisResponse,
  RatioCard,
  RatioTrendPoint,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoClosePeriodBanner } from './CfoClosePeriodBanner'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { useCfoAnalysis } from './analysis'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard } from './analysis/types'
import { groundingFromRatioCard, ratioHealthVisualFromRatioCard } from './cfoRatioHealthVisual'
import { LollipopBenchmark, type LollipopItem } from './charts'
import { CfoHealthScorePanel } from './CfoHealthScorePanel'
import { CfoCardSkeletons, CfoChartSkeleton, CfoControlRow, CfoSegmentedControl } from './CfoPagePrimitives'
import type { CfoShellSelectOption } from './CfoPageShell'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { formatAsOf, formatRatioValue, normalizeCurrencyTextCompact, trimStr } from './cfoFormat'
import {
  RatioDetailCard,
  RatioStat,
  RatioTrendChart,
  RATIO_WINDOW_OPTIONS,
  formatShortMonth,
  ratioBenchmarkAnchor,
  ratioCompanyValueForWindow,
  ratioTone,
  ratioWindowOption,
  type RatioWindow,
} from './ratios/RatioDetailCard'
import {
  ratioBenchmarkAssessmentForScore,
  ratioBenchmarkSource,
  ratioNoBenchmarkNote,
  softenHistoryBenchmarkAssessment,
  type RatioBenchmarkSource,
} from './cfoRatioBenchmarking'
import { PinToReportButton, type AddToReportPayload } from './presentation/useAddToReport'
import { buildRatioComparisonRows } from './cfoRatioComparisonRows'
import { CFO_SURFACES } from './cfoTelemetry'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { getCachedQuickBooksRatios, loadCfoRatiosCompatible } from './cfoRatiosCache'
import { cfoFirmOriginSearch, cfoMetricFocusFromLocation } from './cfoRoutes'
import { useCfoBootstrap } from './useCfoBootstrap'

const SURFACE = CFO_SURFACES.ratios
type RatiosView = 'overview' | 'drivers' | 'sfg'
type SfgTrendPeriod = NonNullable<NonNullable<QuickBooksGrowthAnalysisResponse['trends']>['periods']>[number]
type GrowthScenarioResult = NonNullable<QuickBooksGrowthAnalysisResponse['baseline']>
type DriverMetricId = 'equity_multiplier'
type SfgMetricId = 'annual_sfg' | 'cash_generated_per_sales_dollar' | 'cash_required_per_sales_dollar' | 'cash_conversion_cycle'
type SyntheticMetricId = DriverMetricId | SfgMetricId
type DetailRef = { current: HTMLDivElement | null }
type SyntheticMetricCardModel = {
  id: SyntheticMetricId
  label: string
  value: string
  accent?: string
  description: string
  formula: string
  why: string
  source: string
  components?: Array<{ label: string; value: string }>
  /** Optional 12-month trend for cards whose value is itself a ratio (e.g. equity multiplier). */
  trend?: RatioTrendPoint[]
  /** Optional IRS-derived benchmark to render as an "IRS average" stat + provenance note. */
  benchmarkValue?: string | null
  benchmarkNote?: string | null
}

const scrollCfoPageToNode = (node: HTMLElement) => {
  const scroller = node.closest<HTMLElement>('.cfo-shell-main')
  if (!scroller || scroller.scrollHeight <= scroller.clientHeight + 1) {
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    return
  }
  const topbar = scroller.querySelector<HTMLElement>('.cfo-shell-topbar')
  const scrollerRect = scroller.getBoundingClientRect()
  const nodeRect = node.getBoundingClientRect()
  const topbarHeight = topbar?.getBoundingClientRect().height || 0
  const top = scroller.scrollTop + nodeRect.top - scrollerRect.top - topbarHeight - 16
  if (typeof scroller.scrollTo === 'function') {
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  }
}

const scrollCfoPageToTop = (node: HTMLElement | null) => {
  const scroller = node?.closest<HTMLElement>('.cfo-shell-main')
  if (scroller && scroller.scrollHeight > scroller.clientHeight + 1) {
    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ top: 0, behavior: 'smooth' })
    }
    return
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const KEY_RATIO_ORDER = [
  'current_ratio',
  'quick_ratio',
  'debt_to_equity',
  'debt_to_assets',
  'days_sales_outstanding',
  'interest_coverage',
] as const

export type CfoRatiosPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  onPeriodOptionsDiscovered?: (options: CfoShellSelectOption[]) => void
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
  /** Exact ratio selected by the drill action that opened this page. */
  focusMetricId?: string | null
}

const keyRatioRank = (ratio: RatioCard): number => {
  const idx = KEY_RATIO_ORDER.indexOf(ratio.id as (typeof KEY_RATIO_ORDER)[number])
  return idx === -1 ? KEY_RATIO_ORDER.length : idx
}

type BenchmarkedRatio = {
  ratio: RatioCard
  anchor: number
  companyValue: number
  score: number
}
type ChartBenchmarkedRatio = BenchmarkedRatio & { groupLabel?: string }

const buildBenchmarkedRatio = (ratio: RatioCard, window: RatioWindow): BenchmarkedRatio | null => {
  const value = ratioCompanyValueForWindow(ratio, window)
  const anchor = ratioBenchmarkAnchor(ratio)
  if (value == null || !Number.isFinite(value) || anchor == null) return null
  if (anchor > 0) {
    if (ratio.higher_is_better === false) {
      if (value <= 0) return { ratio, anchor, companyValue: value, score: 2.5 }
      return { ratio, anchor, companyValue: value, score: anchor / value }
    }
    return { ratio, anchor, companyValue: value, score: value / anchor }
  }
  // Zero/negative anchors (own-history averages of offset metrics like "Legacy P&L Noise"):
  // a straight division is meaningless or sign-flipped there, so score by distance from the
  // anchor scaled to its magnitude — 1.0 still means "at benchmark" and the good side of the
  // anchor still lands right of the guide.
  const scaleBase = Math.abs(anchor) > 1e-9 ? Math.abs(anchor) : Math.max(Math.abs(value), 1)
  const distance = (value - anchor) / scaleBase
  return {
    ratio,
    anchor,
    companyValue: value,
    score: ratio.higher_is_better === false ? 1 - distance : 1 + distance,
  }
}

const normalizeAsOfPeriod = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || '').trim())
  return match ? `${match[1]}-${match[2]}-01` : ''
}

/** Benchmark-column text: band anchors carry their direction (≥ floor / ≤ ceiling). */
const benchmarkDisplayForAnchor = (
  ratio: RatioCard,
  anchor: number,
  source: RatioBenchmarkSource | null,
): string => {
  const value = formatRatioValue(anchor, ratio.unit)
  if (source?.kind !== 'band') return value
  return `${ratio.higher_is_better === false ? '≤' : '≥'} ${value}`
}

/** Per-workbook localStorage key for the user's hidden overview metrics. */
const hiddenRatioStorageKey = (workbookId: string) => `fgcfo:ratio-hidden:${workbookId}`

const readHiddenRatioIds = (workbookId: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(hiddenRatioStorageKey(workbookId))
    const parsed = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

const buildRatioPeriodOptions = (ratios: RatioAnalysisResponse | null): CfoShellSelectOption[] => {
  const periods = new Set<string>()
  if (ratios) {
    const asOf = normalizeAsOfPeriod(ratios.as_of)
    if (asOf) periods.add(asOf)
    for (const group of ratios.categories || []) {
      for (const ratio of group.ratios || []) {
        const latest = normalizeAsOfPeriod(ratio.latest_period)
        if (latest) periods.add(latest)
        for (const point of ratio.trend || []) {
          const period = normalizeAsOfPeriod(point.period)
          if (period) periods.add(period)
        }
      }
    }
  }
  return [...periods]
    .sort((a, b) => b.localeCompare(a))
    .map((period) => ({ id: period, label: formatAsOf(period) }))
}

const mergePeriodOptions = (
  current: CfoShellSelectOption[],
  incoming: CfoShellSelectOption[],
): CfoShellSelectOption[] => {
  const byId = new Map<string, CfoShellSelectOption>()
  for (const option of current) byId.set(option.id, option)
  for (const option of incoming) byId.set(option.id, option)
  return [...byId.values()].sort((a, b) => b.id.localeCompare(a.id))
}

const benchmarkBasisLabel = (ratios: RatioAnalysisResponse | null): string => {
  const profile = ratios?.benchmark_profile
  if (profile) return 'IRS industry avg'
  return 'healthy benchmark'
}

const optionText = (option: { naics_code: string; industry_title: string }): string =>
  `${option.naics_code} · ${option.industry_title}`

export function CfoRatiosPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions: globalPeriodOptions = [],
  onPeriodOptionsDiscovered,
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
  focusMetricId = null,
}: CfoRatiosPageProps) {
  const analysis = useCfoAnalysis()
  const [ratios, setRatios] = useState<RatioAnalysisResponse | null>(null)
  const [growth, setGrowth] = useState<QuickBooksGrowthAnalysisResponse | null>(null)
  const [growthLoading, setGrowthLoading] = useState(true)
  const [industryProfile, setIndustryProfile] = useState<IndustryBenchmarkProfileResponse | null>(null)
  const [industryOpen, setIndustryOpen] = useState(false)
  const [industrySearch, setIndustrySearch] = useState('')
  const [industrySaving, setIndustrySaving] = useState(false)
  const [view, setView] = useState<RatiosView>('overview')
  const [ratioWindow, setRatioWindow] = useState<RatioWindow>('latest')
  const [healthDetailId, setHealthDetailId] = useState<string | null>(null)
  const [selectedRatioId, setSelectedRatioId] = useState<string | null>(null)
  const [showAllRatios, setShowAllRatios] = useState(false)
  const [hiddenRatioIds, setHiddenRatioIds] = useState<Set<string>>(new Set())
  const [hiddenOpen, setHiddenOpen] = useState(false)
  const [selectedSyntheticMetricId, setSelectedSyntheticMetricId] = useState<SyntheticMetricId | null>(null)
  const [localPeriodOptions, setLocalPeriodOptions] = useState<CfoShellSelectOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [growthError, setGrowthError] = useState('')
  const [industryError, setIndustryError] = useState('')
  const { bootstrap } = useCfoBootstrap(appApi, workbookId)
  const isQuickBooksProvider = !bootstrap?.accounting_provider || bootstrap.accounting_provider === 'quickbooks'
  const industryAutoDetectKeyRef = useRef<string | null>(null)
  const mountedRef = useRef(false)
  const workbookIdRef = useRef('')
  const selectedEndPeriodRef = useRef<string | null>(null)
  const inFlightRatiosRef = useRef<{ workbookId: string; period: string | null } | null>(null)
  const loadedRatiosRef = useRef<{ workbookId: string; period: string | null } | null>(null)
  const detailRef = useRef<HTMLDivElement | null>(null)
  const appliedFirmFocusRef = useRef('')
  const locationFocus = cfoMetricFocusFromLocation(typeof window === 'undefined' ? null : window.location)
  const routeFocus = {
    metricId: trimStr(focusMetricId) || locationFocus.metricId,
    text: locationFocus.text,
  }
  const openedFromFirmCockpit = cfoFirmOriginSearch(typeof window === 'undefined' ? null : window.location) !== ''
  workbookIdRef.current = trimStr(workbookId)
  selectedEndPeriodRef.current = normalizeAsOfPeriod(selectedEndPeriod) || null

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Hidden overview metrics are a per-workbook user preference, restored on every visit.
  useEffect(() => {
    const wb = trimStr(workbookId)
    setHiddenRatioIds(wb ? readHiddenRatioIds(wb) : new Set())
    setHiddenOpen(false)
  }, [workbookId])

  const updateHiddenRatioIds = (updater: (prev: Set<string>) => Set<string>) => {
    setHiddenRatioIds((prev) => {
      const next = updater(prev)
      const wb = workbookIdRef.current
      if (wb) {
        try {
          window.localStorage.setItem(hiddenRatioStorageKey(wb), JSON.stringify([...next]))
        } catch {
          // best-effort persistence — hiding still works for this session
        }
      }
      return next
    })
  }
  const hideRatio = (id: string) => updateHiddenRatioIds((prev) => new Set(prev).add(id))
  const showRatio = (id: string) =>
    updateHiddenRatioIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

  useEffect(() => {
    setLocalPeriodOptions([])
    setIndustryProfile(null)
    setIndustrySearch('')
    setIndustryOpen(false)
    setSelectedRatioId(null)
    setSelectedSyntheticMetricId(null)
    setGrowth(null)
    setGrowthLoading(true)
    setGrowthError('')
    inFlightRatiosRef.current = null
    loadedRatiosRef.current = null
    industryAutoDetectKeyRef.current = null
  }, [workbookId])

  useEffect(() => {
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    const shellPeriod = globalPeriodOptions.find((option) => normalizeAsOfPeriod(option.id))?.id || null
    const requestedPeriod = normalizeAsOfPeriod(selectedEndPeriod) || normalizeAsOfPeriod(shellPeriod)
    const loadedRatios = loadedRatiosRef.current
    if (loadedRatios?.workbookId === wb && loadedRatios.period === (requestedPeriod || null)) {
      setLoading(false)
      return
    }
    const inFlightRatios = inFlightRatiosRef.current
    if (inFlightRatios?.workbookId === wb && inFlightRatios.period === (requestedPeriod || null)) {
      setLoading(true)
      return
    }
    setLoading(true)
    setError('')
    setGrowthError('')
    setRatios(null)
    inFlightRatiosRef.current = { workbookId: wb, period: requestedPeriod || null }
    getCachedQuickBooksRatios(appApi, wb, requestedPeriod)
      .then((nextRatios) => {
        if (!mountedRef.current || workbookIdRef.current !== wb) return
        const responsePeriod = normalizeAsOfPeriod(nextRatios.as_of)
        const currentRequestedPeriod = selectedEndPeriodRef.current
        const responseMatchesCurrentPeriod =
          !currentRequestedPeriod || responsePeriod === currentRequestedPeriod || requestedPeriod === currentRequestedPeriod
        if (!responseMatchesCurrentPeriod) return
        setRatios(nextRatios)
        loadedRatiosRef.current = {
          workbookId: wb,
          period: responsePeriod || requestedPeriod || null,
        }
        const nextOptions = buildRatioPeriodOptions(nextRatios)
        if (nextOptions.length) {
          setLocalPeriodOptions((current) => mergePeriodOptions(current, nextOptions))
          onPeriodOptionsDiscovered?.(nextOptions)
        }
      })
      .catch((err: any) => {
        if (!mountedRef.current || workbookIdRef.current !== wb) return
        setError(trimStr(err?.message || err) || 'Could not load ratios.')
      })
      .finally(() => {
        const inFlightRatios = inFlightRatiosRef.current
        if (inFlightRatios?.workbookId === wb && inFlightRatios.period === (requestedPeriod || null)) {
          inFlightRatiosRef.current = null
        }
        if (!mountedRef.current || workbookIdRef.current !== wb) return
        setLoading(false)
      })
  }, [appApi, globalPeriodOptions, onPeriodOptionsDiscovered, selectedEndPeriod, workbookId])

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb || !industryOpen || industryProfile || !isQuickBooksProvider) return
    setIndustryError('')
    appApi
      .getQuickBooksIndustryProfile(wb)
      .then((value) => {
        if (!cancelled) setIndustryProfile(value)
      })
      .catch((err: any) => {
        if (!cancelled) setIndustryError(trimStr(err?.message || err) || 'Could not load industry benchmarks.')
      })
    return () => {
      cancelled = true
    }
  }, [appApi, industryOpen, industryProfile, isQuickBooksProvider, workbookId])

  // Prefetch SFG/growth on mount and on period/workbook change — not gated on the SFG tab being open —
  // so the panel is populated the moment the user switches to it. The panel also gets its own
  // growthLoading state (below) so it shows a skeleton instead of an empty panel while the fetch runs.
  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb || !isQuickBooksProvider) {
      setGrowth(null)
      setGrowthLoading(false)
      return
    }
    setGrowthError('')
    setGrowthLoading(true)
    appApi.getQuickBooksCfoGrowthAnalysis(wb, selectedEndPeriod ? { asOf: selectedEndPeriod } : undefined)
      .then((value) => {
        if (!cancelled) setGrowth(value)
      })
      .catch((err: any) => {
        if (!cancelled) setGrowthError(trimStr(err?.message || err) || 'Could not load SFG drivers.')
      })
      .finally(() => {
        if (!cancelled) setGrowthLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, isQuickBooksProvider, selectedEndPeriod, workbookId])

  const resolvedAsOf = useMemo(
    () => selectedEndPeriod || normalizeAsOfPeriod(ratios?.as_of || null),
    [selectedEndPeriod, ratios],
  )
  const periodOptions = globalPeriodOptions.length ? globalPeriodOptions : localPeriodOptions
  const periodValue = useMemo(() => {
    if (!resolvedAsOf) return undefined
    return periodOptions.some((option) => option.id === resolvedAsOf) ? resolvedAsOf : undefined
  }, [periodOptions, resolvedAsOf])
  const asOfLabel = useMemo(() => formatAsOf(resolvedAsOf || ratios?.as_of || null), [resolvedAsOf, ratios])
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { openGlDetail, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts,
    onNavSelect,
    period: periodValue ?? null,
  })
  const benchmarkProfile = ratios?.benchmark_profile || industryProfile?.selected || industryProfile?.candidates?.[0] || null
  const benchmarkLabel = benchmarkBasisLabel(ratios)
  const cacheState = ratios
    ? { tone: 'ready' as const, label: 'Live · cached', title: 'CFO ratio analytics ready' }
    : null
  const industryOptions = useMemo(() => {
    const options = industryProfile?.options || []
    const q = industrySearch.trim().toLowerCase()
    if (!q) return options.slice(0, 80)
    return options
      .filter((option) => option.naics_code.includes(q) || option.industry_title.toLowerCase().includes(q))
      .slice(0, 80)
  }, [industryProfile, industrySearch])
  const [selectedIndustryCode, setSelectedIndustryCode] = useState('')

  useEffect(() => {
    const code = industryProfile?.selected?.naics_code || industryProfile?.candidates?.[0]?.naics_code || ''
    setSelectedIndustryCode(code)
  }, [industryProfile])

  const reloadRatios = async () => {
    const wb = trimStr(workbookId)
    if (!wb) return
    const requestedPeriod = normalizeAsOfPeriod(selectedEndPeriod)
    const opts = requestedPeriod ? { grain: 'month' as const, asOf: requestedPeriod } : { grain: 'month' as const }
    const next = await loadCfoRatiosCompatible(appApi, wb, opts)
    setRatios(next)
    loadedRatiosRef.current = {
      workbookId: wb,
      period: normalizeAsOfPeriod(next.as_of) || requestedPeriod || null,
    }
  }

  // Inline AI-change badge actions (revert = reject the belief that changed the card; redirect =
  // re-author it). Both refetch the ratio set so the card reflects the new state immediately.
  const [aiChangeBusy, setAiChangeBusy] = useState(false)
  const revertAiChange = async (claimId: string) => {
    const wb = trimStr(workbookId)
    if (!wb || !claimId) return
    setAiChangeBusy(true)
    try {
      await appApi.rejectUnderstandingClaim(wb, claimId)
      await reloadRatios()
    } catch {
      // best-effort — a failed revert simply leaves the AI change in place
    } finally {
      setAiChangeBusy(false)
    }
  }
  const redirectAiChange = async (originId: string, instruction: string) => {
    const wb = trimStr(workbookId)
    const text = (instruction || '').trim()
    if (!wb || !originId || !text) return
    setAiChangeBusy(true)
    try {
      await appApi.redirectUnderstanding(wb, originId, text)
      await reloadRatios()
    } catch {
      // best-effort
    } finally {
      setAiChangeBusy(false)
    }
  }

  const saveHealthWeights = async (weights: Record<string, number>) => {
    const wb = trimStr(workbookId)
    if (!wb) return
    setAiChangeBusy(true)
    try {
      await appApi.saveHealthCategoryWeights(wb, weights)
      await reloadRatios()
    } catch {
      // best-effort — a failed save leaves the current weighting in place
    } finally {
      setAiChangeBusy(false)
    }
  }

  const saveHealthRatioWeights = async (_categoryKey: string, ratioWeights: Record<string, number>) => {
    const wb = trimStr(workbookId)
    if (!wb) return
    setAiChangeBusy(true)
    try {
      await appApi.saveHealthRatioWeights(wb, ratioWeights)
      await reloadRatios()
    } catch {
      // best-effort — a failed save leaves the current weighting in place
    } finally {
      setAiChangeBusy(false)
    }
  }

  const toggleHealthGrades = async (show: boolean) => {
    const wb = trimStr(workbookId)
    if (!wb) return
    setAiChangeBusy(true)
    try {
      await appApi.setCfoDisplayPrefs(wb, show)
      await reloadRatios()
    } catch {
      // best-effort
    } finally {
      setAiChangeBusy(false)
    }
  }

  const saveIndustryProfile = async (naicsCode?: string, source = 'user_override') => {
    const wb = trimStr(workbookId)
    const code = trimStr(naicsCode || selectedIndustryCode)
    if (!wb || !code || !isQuickBooksProvider) return
    setIndustrySaving(true)
    setIndustryError('')
    try {
      const next = await appApi.updateQuickBooksIndustryProfile(wb, {
        naics_code: code,
        selection_source: source,
        confidence: source === 'user_override' ? 1 : null,
        evidence_json: { source: source === 'user_override' ? 'user_selected_industry' : source },
      })
      setIndustryProfile(next)
      await reloadRatios()
      setIndustryOpen(false)
    } catch (err: any) {
      setIndustryError(trimStr(err?.message || err) || 'Could not save industry benchmark.')
    } finally {
      setIndustrySaving(false)
    }
  }

  const detectIndustryWithWeb = async () => {
    const wb = trimStr(workbookId)
    if (!wb || !isQuickBooksProvider) return
    setIndustrySaving(true)
    setIndustryError('')
    try {
      const next = await appApi.detectQuickBooksIndustryProfile(wb, { web_search: true, persist: true })
      setIndustryProfile(next)
      await reloadRatios()
      setIndustryOpen(false)
    } catch (err: any) {
      setIndustryError(trimStr(err?.message || err) || 'Could not detect industry.')
    } finally {
      setIndustrySaving(false)
    }
  }

  useEffect(() => {
    const wb = trimStr(workbookId)
    if (!wb || !industryProfile || !isQuickBooksProvider) return
    if (industryProfile.selected || (industryProfile.candidates || []).length > 0) return
    const key = `${wb}:${industryProfile.connection_id}:${industryProfile.company_name || ''}`
    if (industryAutoDetectKeyRef.current === key) return
    industryAutoDetectKeyRef.current = key

    let cancelled = false
    const opts = selectedEndPeriod ? { grain: 'month' as const, asOf: selectedEndPeriod } : { grain: 'month' as const }
    setIndustrySaving(true)
    setIndustryError('')
    appApi
      .detectQuickBooksIndustryProfile(wb, { web_search: true, persist: true })
      .then(async (next) => {
        const nextRatios = await loadCfoRatiosCompatible(appApi, wb, opts)
        if (cancelled) return
        setIndustryProfile(next)
        setRatios(nextRatios)
        loadedRatiosRef.current = {
          workbookId: wb,
          period: normalizeAsOfPeriod(nextRatios.as_of) || normalizeAsOfPeriod(selectedEndPeriod) || null,
        }
      })
      .catch((err: any) => {
        if (cancelled) return
        setIndustryError(trimStr(err?.message || err) || 'Could not detect industry automatically. Choose one from the IRS catalog.')
      })
      .finally(() => {
        if (!cancelled) setIndustrySaving(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, industryProfile, selectedEndPeriod, workbookId])

  const allRatios = useMemo<RatioCard[]>(
    // Hide ratios the AI de-prioritized: it auto-REPLACES them with its better metric, so they drop
    // out of the cards entirely. They stay live as beliefs (revert / add-back lives on the AI Tuning
    // tab), and the architect's custom focus metrics remain in the list in their place.
    () => (ratios?.categories || []).flatMap((g) => g.ratios).filter((r) => r.ai_change?.kind !== 'demoted'),
    [ratios],
  )
  const ratioById = useMemo(() => {
    const map = new Map<string, RatioCard>()
    for (const ratio of allRatios) map.set(ratio.id, ratio)
    return map
  }, [allRatios])

  useEffect(() => {
    if (!allRatios.length || (!routeFocus.metricId && !routeFocus.text)) return
    const focusKey = `${trimStr(workbookId)}:${routeFocus.metricId}:${routeFocus.text}`
    if (appliedFirmFocusRef.current === focusKey) return
    const metricId = routeFocus.metricId.toLowerCase()
    const focusText = routeFocus.text.toLowerCase()
    const focused = allRatios.find((ratio) => ratio.id.toLowerCase() === metricId)
      || [...allRatios]
        .sort((a, b) => b.label.length - a.label.length)
        .find((ratio) => focusText.includes(ratio.label.toLowerCase()))
    if (!focused) return
    appliedFirmFocusRef.current = focusKey
    setView('overview')
    setSelectedSyntheticMetricId(null)
    setSelectedRatioId(focused.id)
    setHiddenRatioIds((current) => {
      if (!current.has(focused.id)) return current
      const next = new Set(current)
      next.delete(focused.id)
      try {
        window.localStorage.setItem(hiddenRatioStorageKey(trimStr(workbookId)), JSON.stringify([...next]))
      } catch {
        // The exact cockpit issue still opens even if preference persistence is unavailable.
      }
      return next
    })
  }, [allRatios, routeFocus.metricId, routeFocus.text, workbookId])

  const benchmarkedRatios = useMemo<BenchmarkedRatio[]>(() => {
    return allRatios
      .map((ratio) => buildBenchmarkedRatio(ratio, ratioWindow))
      .filter((entry): entry is BenchmarkedRatio => entry != null)
      .sort((a, b) => {
        const keyDiff = keyRatioRank(a.ratio) - keyRatioRank(b.ratio)
        if (keyDiff !== 0) return keyDiff
        return Math.abs(1 - b.score) - Math.abs(1 - a.score)
      })
  }, [allRatios, ratioWindow])

  const benchmarkedById = useMemo(() => {
    const map = new Map<string, BenchmarkedRatio>()
    for (const entry of benchmarkedRatios) map.set(entry.ratio.id, entry)
    return map
  }, [benchmarkedRatios])

  const selectedRatio = selectedRatioId ? ratioById.get(selectedRatioId) || null : null
  const chartRatios = useMemo<ChartBenchmarkedRatio[]>(() => {
    const grouped: ChartBenchmarkedRatio[] = []
    for (const group of ratios?.categories || []) {
      let first = true
      for (const ratio of group.ratios) {
        if (hiddenRatioIds.has(ratio.id)) continue
        const entry = benchmarkedById.get(ratio.id)
        if (!entry) continue
        grouped.push({ ...entry, groupLabel: first ? group.label : undefined })
        first = false
      }
    }
    return grouped
  }, [benchmarkedById, hiddenRatioIds, ratios])

  const ratioInstantCards = useMemo<CfoInstantCard[]>(() => {
    const sourceFingerprint = trimStr(ratios?.signature) || trimStr((ratios as any)?.freshness_token)
    return allRatios.map((ratio) => {
      const comparisonRows = buildRatioComparisonRows(ratio).map(([label, current, prior, change]) => ({
        label,
        current,
        prior,
        change,
        tone: change.startsWith('+') ? 'good' : change.startsWith('-') || change.startsWith('−') ? 'bad' : 'neutral',
      }))
      return buildCfoInstantCardFromContext({
        context: {
          workbookId,
          pageId: 'ratios',
          activeSection: SURFACE,
          period: periodValue ?? ratio.latest_period ?? ratios?.as_of ?? null,
          comparison: comparisonKind,
          asOfLabel,
          metricId: ratio.id,
          metricLabel: ratio.label,
          sourceComponent: 'ratio_detail_card',
          sourceTables: ['qbo_fpa_cfo_ratio_analysis'],
          selection: {
            ratio_id: ratio.id,
            ratio_window: ratioWindow,
            ratio_card: ratio,
            signature: sourceFingerprint || null,
            source_fingerprint: sourceFingerprint || null,
            instant_card_comparisons: comparisonRows,
            instant_card_insights: [
              ratio.ai_interpretation || ratio.interpretation || `${ratio.label} is ${formatRatioValue(ratio.latest ?? ratioCompanyValueForWindow(ratio, ratioWindow), ratio.unit)}.`,
            ],
            instant_card_prescriptive_actions: [
              ratio.assessment === 'healthy'
                ? `Keep monitoring ${ratio.label.toLowerCase()} for deterioration across the next month and last-3-month windows.`
                : `Inspect the component inputs behind ${ratio.label.toLowerCase()} and decide which operating lever needs attention this month.`,
            ],
            artifact_ids: [sourceFingerprint].filter(Boolean),
          },
          grounding: groundingFromRatioCard(ratio, ratioWindow),
          instantCard: null,
        },
        visual: ratioHealthVisualFromRatioCard(ratio, ratioWindow),
      })
    })
  }, [allRatios, asOfLabel, comparisonKind, periodValue, ratioWindow, ratios, workbookId])

  const defaultOverviewRatio = useMemo(
    () => chartRatios[0]?.ratio || allRatios.find((ratio) => ratio.latest != null) || allRatios[0] || null,
    [allRatios, chartRatios],
  )
  const overviewDetailRatio = selectedRatio || defaultOverviewRatio
  const overviewSelectedRatioId = selectedRatioId || overviewDetailRatio?.id || null
  const healthDetailRatio = healthDetailId
    ? (ratios?.categories.flatMap((g) => g.ratios).find((r) => r.id === healthDetailId) ?? null)
    : null
  const selectRatio = (id: string) => {
    setSelectedSyntheticMetricId(null)
    setSelectedRatioId(id)
  }
  const selectSyntheticMetric = (id: SyntheticMetricId) => {
    setSelectedRatioId(null)
    setSelectedSyntheticMetricId(id)
  }
  const scrollRatiosOverviewIntoView = () => scrollCfoPageToTop(detailRef.current)
  const openRatioAnalysis = (ratio: RatioCard) => {
    analysis.openAnalysis({
      metricId: ratio.id,
      metricLabel: ratio.label,
      sourceComponent: 'ratio_detail_card',
      // Pass the full card so the modal embeds it instantly (no refetch).
      selection: {
        ratio_id: ratio.id,
        ratio_window: ratioWindow,
        ratio_card: ratio,
        instant_card_comparisons: buildRatioComparisonRows(ratio).map(([label, current, prior, change]) => ({
          label,
          current,
          prior,
          change,
          tone: change.startsWith('+') ? 'good' : change.startsWith('-') || change.startsWith('−') ? 'bad' : 'neutral',
        })),
        instant_card_insights: [
          ratio.ai_interpretation || ratio.interpretation || `${ratio.label} is ${formatRatioValue(ratio.latest ?? ratioCompanyValueForWindow(ratio, ratioWindow), ratio.unit)}.`,
        ],
        instant_card_prescriptive_actions: [
          ratio.assessment === 'healthy'
            ? `Keep monitoring ${ratio.label.toLowerCase()} for deterioration across the next month and last-3-month windows.`
            : `Inspect the component inputs behind ${ratio.label.toLowerCase()} and decide which operating lever needs attention this month.`,
        ],
      },
      visual: ratioHealthVisualFromRatioCard(ratio, ratioWindow),
      grounding: groundingFromRatioCard(ratio, ratioWindow),
      label: ratio.label,
      seedPrompt: [
        `Give a short, grounded read on ${ratio.label}.`,
        'Treat workspace_context.grounding (value, healthy range, trend, assessment, interpretation) as authoritative. In 1-2 sentences say whether it is healthy and the single main driver, then one concrete next step. Do not hedge about local data.',
      ].join(' '),
    })
  }

  // When a callout is chosen, bring its metric card into view.
  useEffect(() => {
    if (!selectedRatioId && !selectedSyntheticMetricId) return
    const node = detailRef.current
    if (!node) return
    const id = window.requestAnimationFrame(() => {
      scrollCfoPageToNode(node)
    })
    return () => window.cancelAnimationFrame(id)
  }, [selectedRatioId, selectedSyntheticMetricId, view])

  // Headline lollipop: normalized ratios where 1.0 means "at benchmark".
  const lollipopItems = useMemo<LollipopItem[]>(() => {
    return chartRatios.map((entry) => {
      const { ratio, anchor, companyValue, score, groupLabel } = entry
      const source = ratioBenchmarkSource(ratio)
      return {
        id: ratio.id,
        groupLabel,
        label: ratio.label,
        value: score,
        assessment: softenHistoryBenchmarkAssessment(
          ratioBenchmarkAssessmentForScore(score, ratio.higher_is_better != null),
          source,
        ),
        display: formatRatioValue(companyValue, ratio.unit),
        companyDisplay: formatRatioValue(companyValue, ratio.unit),
        benchmarkDisplay: benchmarkDisplayForAnchor(ratio, anchor, source),
        benchmarkSource: source?.kind,
        benchmarkTag: source?.tag,
      }
    })
  }, [chartRatios])

  // Expanded view: the full ratio library grouped by category. Benchmarked rows keep their
  // normalized dot; unbenchmarked ones (NaN score) render label + company value only. The
  // headline stats and report payload stay on the benchmarked set regardless of expansion.
  const lollipopItemsAll = useMemo<LollipopItem[]>(() => {
    const items: LollipopItem[] = []
    for (const group of ratios?.categories || []) {
      let first = true
      for (const ratio of group.ratios) {
        if (ratio.ai_change?.kind === 'demoted') continue
        if (hiddenRatioIds.has(ratio.id)) continue
        const entry = benchmarkedById.get(ratio.id)
        const companyValue = entry ? entry.companyValue : ratioCompanyValueForWindow(ratio, ratioWindow)
        const companyDisplay = companyValue != null ? formatRatioValue(companyValue, ratio.unit) : '—'
        const source = entry ? ratioBenchmarkSource(ratio) : null
        items.push({
          id: ratio.id,
          groupLabel: first ? group.label : undefined,
          label: ratio.label,
          value: entry ? entry.score : Number.NaN,
          assessment: entry
            ? softenHistoryBenchmarkAssessment(
                ratioBenchmarkAssessmentForScore(entry.score, ratio.higher_is_better != null),
                source,
              )
            : ratio.assessment,
          display: companyDisplay,
          companyDisplay,
          benchmarkDisplay: entry ? benchmarkDisplayForAnchor(ratio, entry.anchor, source) : undefined,
          benchmarkSource: source?.kind,
          benchmarkTag: source?.tag,
          noBenchmarkNote: entry ? undefined : ratioNoBenchmarkNote(ratio),
        })
        first = false
      }
    }
    return items
  }, [benchmarkedById, hiddenRatioIds, ratioWindow, ratios])

  const hiddenRatios = useMemo(
    () => allRatios.filter((ratio) => hiddenRatioIds.has(ratio.id)),
    [allRatios, hiddenRatioIds],
  )
  const concerns = useMemo(() => allRatios.filter((r) => r.assessment === 'concern').length, [allRatios])
  const atOrAboveBenchmark = useMemo(
    () => lollipopItems.filter((item) => item.value >= 1).length,
    [lollipopItems],
  )
  // Sources now mix per row (IRS where available, own trailing average
  // otherwise), so the headline noun is the generic "benchmark" even when an
  // industry profile is selected.
  const benchmarkNoun = 'benchmark'
  const windowLabel = ratioWindowOption(ratioWindow).label

  // Tag the benchmark chart into the board pack as a grounded text caption (the
  // presentation block model has no lollipop kind; the caption carries the data).
  const ratiosReportPayload = (): AddToReportPayload => ({
    title: 'Ratio benchmarks',
    kind: 'text',
    section: 'home',
    source: { metric: 'ratio_benchmarks', window: ratioWindow },
    text: [
      `${atOrAboveBenchmark} of ${lollipopItems.length} key ratios at or above the ${benchmarkNoun} (${windowLabel.toLowerCase()}).`,
      lollipopItems
        .slice(0, 8)
        .map((item) => `${item.label}: ${item.companyDisplay || item.display || '—'} vs ${item.benchmarkDisplay || benchmarkNoun}${item.assessment && item.assessment !== 'neutral' ? ` (${item.assessment})` : ''}`)
        .join('; '),
    ].join(' '),
  })
  // Threshold ladder, not a binary all-pass/else. The old two-way ternary printed "Mostly healthy"
  // for ANY count below the total — including 0 of N, which reads as nonsense against "0 at or above".
  const benchmarkStanding = (() => {
    const total = lollipopItems.length
    if (!total) return ''
    if (atOrAboveBenchmark === total) return 'Healthy'
    if (atOrAboveBenchmark === 0) return 'Needs attention'
    return atOrAboveBenchmark / total >= 0.6 ? 'Mostly healthy' : 'Mixed'
  })()
  const heroHeadline = ratios
    ? lollipopItems.length
      ? `${benchmarkStanding} - ${atOrAboveBenchmark} of ${lollipopItems.length} key ratios at or above ${benchmarkNoun}`
      : concerns > 0
        ? `${concerns} ratio${concerns === 1 ? '' : 's'} sit outside their healthy band`
        : 'Your ratios are inside their healthy bands'
    : 'How your ratios compare to healthy ranges'

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="ratios"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Understand"
      title="Ratios & trends"
      lede={
        <>
          “How the business reads against healthy benchmarks — <em>every ratio opens from the overview.</em>”
        </>
      }
      crumb={
        <>
          <b>Ratios &amp; trends</b> · Health overview
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={periodValue}
      onPeriodChange={(value) => {
        const next = normalizeAsOfPeriod(value)
        if (next) onSelectedEndPeriodChange?.(next)
      }}
      periodAriaLabel="Select ratios month"
      comparisonLabel={comparisonOptions.find((option) => option.id === comparisonKind)?.label || 'vs. prior month'}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={cacheState}
      pageLoading={loading}
      pageLoadingLabel="Loading ratio analysis…"
      copilotSubtitle="reading your ratios"
      copilotPlaceholder="Ask about Ratios…"
      copilotIntro="You’re on Ratios. Ask me what a ratio means, why it moved, or how it compares to benchmark."
      askPlaceholder="Ask about a ratio…"
      suggestedPrompts={['Which ratios are concerning?', 'Explain my ROE drivers', 'How is liquidity trending?']}
      duckDbOpts={duckDbOpts}
      instantCards={ratioInstantCards}
      pageFactsFingerprint={trimStr(ratios?.signature) || null}
      openGlDetail={openGlDetail}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        <>
          {glDetailModal}
          <CfoClosePeriodBanner appApi={appApi} workbookId={workbookId} />
          <CfoControlRow>
            <CfoSegmentedControl
              label="Ratios view"
              value={view}
              onChange={(value) => {
                setView(value as RatiosView)
                setSelectedRatioId(null)
                setSelectedSyntheticMetricId(null)
              }}
              options={[
                { id: 'overview', label: 'Health overview' },
                { id: 'drivers', label: 'Drivers (DuPont)' },
                { id: 'sfg', label: 'SFG' },
              ]}
            />
            <label className="cfo-shell-chip" style={{ padding: 0, overflow: 'hidden' }}>
              <select
                aria-label="Select ratio window"
                value={ratioWindow}
                onChange={(event) => setRatioWindow(event.target.value as RatioWindow)}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'inherit',
                  font: 'inherit',
                  fontWeight: 600,
                  minHeight: 34,
                  padding: '0 10px',
                }}
              >
                {RATIO_WINDOW_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
            <button type="button" className="cfo-shell-chip" onClick={() => setIndustryOpen((current) => !current)}>
              {benchmarkProfile ? `Industry · ${benchmarkProfile.industry_title}` : 'Industry · choose'}
            </button>
            <button type="button" className="cfo-shell-chip">{benchmarkLabel}</button>
          </CfoControlRow>

          {industryOpen ? (
            <section
              className="rise"
              style={{
                display: 'grid',
                gap: 'var(--s3)',
                marginBottom: 'var(--s4)',
                padding: 'var(--s4) 0',
                borderTop: '1px solid var(--line)',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--s3)', alignItems: 'end' }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
                  Search industry
                  <input
                    value={industrySearch}
                    onChange={(event) => setIndustrySearch(event.target.value)}
                    placeholder="NAICS or industry"
                    style={{ minHeight: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', background: 'var(--paper)' }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
                  Selected benchmark
                  <select
                    value={selectedIndustryCode}
                    onChange={(event) => setSelectedIndustryCode(event.target.value)}
                    style={{ minHeight: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', background: 'var(--paper)' }}
                  >
                    {industryOptions.map((option) => (
                      <option key={option.naics_code} value={option.naics_code}>{optionText(option)}</option>
                    ))}
                  </select>
                </label>
                <button type="button" className="btn" disabled={industrySaving || !selectedIndustryCode} onClick={() => void saveIndustryProfile()}>
                  Save
                </button>
                <button type="button" className="btn ghost" disabled={industrySaving} onClick={() => void detectIndustryWithWeb()}>
                  Research
                </button>
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                {industryProfile?.catalog
                  ? `${industryProfile.catalog.source_label} ${industryProfile.catalog.source_year} · aggregate industry averages, not medians`
                  : 'Industry benchmarks load from the IRS aggregate catalog.'}
                {industryError ? <span style={{ color: 'var(--neg)', marginLeft: 12 }}>{industryError}</span> : null}
              </div>
            </section>
          ) : null}

          {error ? <div className="cfo-shell-notice error">{error}</div> : null}
          {openedFromFirmCockpit && (routeFocus.metricId || routeFocus.text) ? (
            <div className="cfo-shell-notice" role="status">
              Opened from the firm cockpit{selectedRatio ? ` · ${selectedRatio.label}` : ''}
            </div>
          ) : null}

          {view === 'overview' ? (
            <>
              {ratios?.health ? (
                <CfoHealthScorePanel
                  health={ratios.health}
                  onSaveWeights={saveHealthWeights}
                  onSaveRatioWeights={saveHealthRatioWeights}
                  onToggleGrades={toggleHealthGrades}
                  onSelectRatio={setHealthDetailId}
                  busy={aiChangeBusy}
                />
              ) : null}
              {healthDetailRatio ? (
                <div
                  role="dialog"
                  aria-label={`${healthDetailRatio.label} detail`}
                  onClick={() => setHealthDetailId(null)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1000,
                    background: 'rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    padding: '40px 16px',
                    overflowY: 'auto',
                  }}
                >
                  <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button type="button" className="btn ghost" onClick={() => setHealthDetailId(null)} aria-label="Close" style={{ fontSize: 13 }}>
                        Close ✕
                      </button>
                    </div>
                    <RatioDetailCard
                      ratio={healthDetailRatio}
                      ratioWindow={ratioWindow}
                      onRevertAiChange={revertAiChange}
                      onRedirectAiChange={redirectAiChange}
                      aiChangeBusy={aiChangeBusy}
                    />
                  </div>
                </div>
              ) : null}
              <section className="hero rise">
                <div className="hero-top">
                  <div>
                    <h1>{heroHeadline}</h1>
                    <p style={{ maxWidth: 'none' }}>
                      Each row compares the company {ratioWindow === 'latest' ? 'value' : 'average'} for {windowLabel.toLowerCase()} to the IRS industry average when one exists, otherwise to the company&apos;s own trailing average (ring dots). The detail card opens with the first benchmarked ratio; click any row to change it. Hover a row and click ✕ to hide metrics you don&apos;t need — restore them any time below the chart.
                    </p>
                  </div>
                </div>
                <figure className="figure">
                  <div className="fig-head">
                    <span className="ftitle">Key ratios vs. benchmark</span>
                    <span className="fmeta">{windowLabel} · {ratios?.benchmark_profile ? 'IRS industry average where available, otherwise own trailing average' : 'healthy benchmark or own trailing average'}</span>
                    {lollipopItems.length ? <PinToReportButton buildPayload={ratiosReportPayload} /> : null}
                  </div>
                  {loading ? (
                    <CfoChartSkeleton label="Loading ratio benchmark chart…" style={{ minHeight: 300 }} />
                  ) : lollipopItems.length || hiddenRatios.length ? (
                    <div className="fg-ratio-benchmark-scroll">
                      <LollipopBenchmark
                        className="fig-svg"
                        ariaLabel="Key financial ratios normalized against the industry benchmark line"
                        items={showAllRatios ? lollipopItemsAll : lollipopItems}
                        maxVisibleRows={10}
                        selectedId={overviewSelectedRatioId}
                        onItemClick={(item) => {
                          if (item.id) {
                            selectRatio(item.id)
                          }
                        }}
                        onItemHide={(item) => {
                          if (item.id) hideRatio(item.id)
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--s2, 8px)', flexWrap: 'wrap', marginTop: 'var(--s2, 8px)' }}>
                        {lollipopItemsAll.length > lollipopItems.length ? (
                          <button
                            type="button"
                            className="btn ghost"
                            aria-expanded={showAllRatios}
                            onClick={() => setShowAllRatios((value) => !value)}
                          >
                            {showAllRatios
                              ? `Show ${lollipopItems.length} benchmarked ratios only ▴`
                              : `Show all ${lollipopItemsAll.length} ratios ▾`}
                          </button>
                        ) : null}
                        {hiddenRatios.length ? (
                          <button
                            type="button"
                            className="btn ghost"
                            aria-expanded={hiddenOpen}
                            onClick={() => setHiddenOpen((value) => !value)}
                          >
                            {hiddenRatios.length} hidden metric{hiddenRatios.length === 1 ? '' : 's'} {hiddenOpen ? '▴' : '▾'}
                          </button>
                        ) : null}
                      </div>
                      {hiddenOpen && hiddenRatios.length ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--s2, 8px)', flexWrap: 'wrap', marginTop: 'var(--s2, 8px)' }}>
                          {hiddenRatios.map((ratio) => (
                            <button
                              key={ratio.id}
                              type="button"
                              className="cfo-shell-chip"
                              title={`Show ${ratio.label} again`}
                              onClick={() => showRatio(ratio.id)}
                            >
                              {ratio.label} ↩
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() => {
                              updateHiddenRatioIds(() => new Set())
                              setHiddenOpen(false)
                            }}
                          >
                            Restore all
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--muted)' }}>The ratio library isn’t available for this workbook yet.</p>
                  )}
                </figure>
              </section>
              {overviewDetailRatio ? (
                <div ref={detailRef} className="fg-ratio-detail-anchor">
                  <div className="fg-ratio-detail-toolbar">
                    <button type="button" className="btn ghost" onClick={scrollRatiosOverviewIntoView}>
                      Back to overview
                    </button>
                    <button type="button" className="btn primary" onClick={() => openRatioAnalysis(overviewDetailRatio)}>
                      Ask the analyst
                    </button>
                  </div>
                  <RatioDetailCard ratio={overviewDetailRatio} ratioWindow={ratioWindow} onRevertAiChange={revertAiChange} onRedirectAiChange={redirectAiChange} aiChangeBusy={aiChangeBusy} />
                </div>
              ) : null}
            </>
          ) : null}

          {view === 'drivers' ? (
            <RatioDriversPanel
              ratios={ratios}
              loading={loading}
              ratioWindow={ratioWindow}
              selectedRatioId={selectedRatioId}
              selectedSyntheticMetricId={selectedSyntheticMetricId}
              onRatioSelect={selectRatio}
              onSyntheticMetricSelect={selectSyntheticMetric}
              detailRef={detailRef}
              onRevertAiChange={revertAiChange}
              onRedirectAiChange={redirectAiChange}
              aiChangeBusy={aiChangeBusy}
            />
          ) : null}

          {view === 'sfg' ? (
            <RatioSfgPanel
              ratios={ratios}
              growth={growth}
              error={growthError}
              loading={growthLoading}
              asOf={resolvedAsOf}
              ratioWindow={ratioWindow}
              selectedRatioId={selectedRatioId}
              selectedSyntheticMetricId={selectedSyntheticMetricId}
              onRatioSelect={selectRatio}
              onSyntheticMetricSelect={selectSyntheticMetric}
              detailRef={detailRef}
              onRevertAiChange={revertAiChange}
              onRedirectAiChange={redirectAiChange}
              aiChangeBusy={aiChangeBusy}
            />
          ) : null}

          {classicEscapes ? (
            <button type="button" className="btn ghost" style={{ marginTop: 'var(--s4)' }} onClick={onOpenClassic}>
              Open ratios workspace
            </button>
          ) : null}
        </>
      )}
    </CfoInteriorPage>
  )
}

function SyntheticMetricCard({ metric }: { metric: SyntheticMetricCardModel }) {
  return (
    <article className="card rise fg-ratio-detail" style={{ borderLeft: `4px solid ${metric.accent || 'var(--brand)'}` }}>
      <div className="fg-ratio-detail-head">
        <div className="fg-ratio-detail-intro">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--fs-h2)' }}>{metric.label}</h3>
            <span className="fg-ratio-pill" style={{ color: metric.accent || 'var(--brand)', background: 'var(--wash)' }}>
              Metric card
            </span>
            {/* Synthetic cards are derived client-side (no server ratio id), so
                they pin into the board pack as a narrative text block. */}
            <PinToReportButton
              buildPayload={() => ({
                title: metric.label,
                kind: 'text',
                section: 'home',
                source: { metric: metric.id },
                text: [`${metric.label}: ${metric.value}.`, metric.description, metric.why].filter(Boolean).join(' '),
              })}
            />
          </div>
          <p className="fg-ratio-explanation">{normalizeCurrencyTextCompact(metric.description)}</p>
          <div className="fg-ratio-formula">{metric.formula}</div>
        </div>
        <div className="fg-ratio-stats">
          <RatioStat label="Latest" value={metric.value} accent={metric.accent || 'var(--brand)'} large />
          {metric.benchmarkValue ? (
            <RatioStat label="IRS average" value={metric.benchmarkValue} large />
          ) : null}
        </div>
      </div>
      {metric.trend && metric.trend.length >= 2 ? (
        <div>
          <div className="fg-ratio-section-label">12-month trend</div>
          <RatioTrendChart
            ratio={{ id: metric.id, label: metric.label, unit: 'times', trend: metric.trend }}
            benchmark={null}
          />
        </div>
      ) : null}
      <div className="fg-ratio-grid">
        <div>
          <div className="fg-ratio-section-label">Why it matters</div>
          <p style={{ margin: 0, color: 'var(--ink-2)', lineHeight: 1.55 }}>{metric.why}</p>
        </div>
        <div>
          <div className="fg-ratio-section-label">Source</div>
          <p style={{ margin: 0, color: 'var(--ink-2)', lineHeight: 1.55 }}>{metric.source}</p>
          {metric.benchmarkNote ? (
            <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 'var(--fs-sm)', lineHeight: 1.5 }}>
              {metric.benchmarkNote}
            </p>
          ) : null}
        </div>
      </div>
      {metric.components?.length ? (
        // Grid, not flex-wrap: RatioStat is width:100%/height:100% (built to fill grid cells), so a
        // plain flex row collapses the tiles into one full-width, over-tall stack. Mirror the overview.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {metric.components.map((component) => (
            <RatioStat key={component.label} label={component.label} value={component.value} />
          ))}
        </div>
      ) : null}
    </article>
  )
}

function buildEquityMultiplierMetric(
  roe: RatioCard | null,
  ratios: RatioCard[],
): SyntheticMetricCardModel | null {
  const component = roe?.components.find((item) => item.id === 'equity_multiplier')
  if (!component) return null
  // Derive the IRS "equity multiplier" benchmark from the debt_to_assets card's IRS average:
  //   equity_multiplier = total_assets / total_equity = 1 / (1 − debt_to_assets).
  // The IRS SOI catalog has no equity_multiplier column, but it does carry debt_to_assets, and that
  // card already ships its IRS benchmark.value. Guard dta in [0, 1) so we never divide by zero or emit
  // a negative/absurd multiplier for a fully debt-funded peer set.
  const dtaCard = ratios.find((r) => r.id === 'debt_to_assets')
  const dta = dtaCard?.benchmark.value
  const benchmarkNum =
    dta != null && Number.isFinite(dta) && dta >= 0 && dta < 1 ? 1 / (1 - dta) : null
  return {
    id: 'equity_multiplier',
    label: component.label || 'Equity Multiplier',
    value: formatRatioValue(component.value, component.unit),
    accent: 'var(--brand)',
    description: 'Assets supported by each dollar of owner equity; the leverage leg of DuPont ROE.',
    formula: 'Total Assets / Total Equity',
    why: 'A higher multiplier can lift ROE through leverage, but it also concentrates balance-sheet risk. Read it with debt ratios to separate efficient capital structure from overextension.',
    source: 'Balance sheet total assets and total equity for the selected period.',
    trend: component.trend,
    benchmarkValue: benchmarkNum != null ? formatRatioValue(benchmarkNum, component.unit) : null,
    benchmarkNote:
      benchmarkNum != null && dtaCard
        ? `Derived from the ${dtaCard.benchmark.industry_label ?? 'IRS'} debt-to-assets average of ${formatRatioValue(dta!, dtaCard.unit)} (equity multiplier = 1 / (1 − debt/assets)).`
        : null,
  }
}

function RatioDriversPanel({
  ratios,
  loading,
  ratioWindow,
  selectedRatioId,
  selectedSyntheticMetricId,
  onRatioSelect,
  onSyntheticMetricSelect,
  detailRef,
  onRevertAiChange,
  onRedirectAiChange,
  aiChangeBusy,
}: {
  ratios: RatioAnalysisResponse | null
  loading: boolean
  ratioWindow: RatioWindow
  selectedRatioId: string | null
  selectedSyntheticMetricId: SyntheticMetricId | null
  onRatioSelect: (id: string) => void
  onSyntheticMetricSelect: (id: SyntheticMetricId) => void
  detailRef: DetailRef
  onRevertAiChange?: (claimId: string, originId: string) => void
  onRedirectAiChange?: (originId: string, instruction: string) => void
  aiChangeBusy?: boolean
}) {
  if (!ratios && loading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--s5)' }}>
        <section className="hero rise">
          <CfoChartSkeleton label="Loading DuPont driver chart…" style={{ minHeight: 190 }} />
        </section>
        <CfoCardSkeletons count={2} label="Loading ratio driver cards…" />
      </div>
    )
  }
  const all = ratios?.categories.flatMap((group) => group.ratios).filter((r) => r.ai_change?.kind !== 'demoted') || []
  const ratioById = new Map(all.map((ratio) => [ratio.id, ratio]))
  // A DuPont driver (e.g. asset_turnover) can be a ratio the AI demoted out of the library list. It is
  // still a complete, renderable RatioCard, and drilling into it from its own driver row must work —
  // demotion should only hide it from the list, not break drill-down. Resolve driver clicks against an
  // UNFILTERED map so a demoted leg still opens its detail card instead of silently no-op'ing.
  const ratioByIdAll = new Map((ratios?.categories.flatMap((group) => group.ratios) || []).map((ratio) => [ratio.id, ratio]))
  const roe = all.find((ratio) => ratio.id === 'return_on_equity' || ratio.id.includes('roe')) || null
  const ccc = all.find((ratio) => ratio.id === 'cash_conversion_cycle') || null
  const cccFormula = ccc?.formula || 'DSO + DIO - DPO'
  const selectedDetailRatio = selectedRatioId ? ratioByIdAll.get(selectedRatioId) || null : null
  const selectedDriverMetric = selectedSyntheticMetricId === 'equity_multiplier' ? buildEquityMultiplierMetric(roe, all) : null
  const detailRatio = selectedDriverMetric ? null : (selectedDetailRatio || roe)
  const selectComponent = (component: RatioCard['components'][number]) => {
    if (ratioByIdAll.has(component.id)) {
      onRatioSelect(component.id)
    } else if (component.id === 'equity_multiplier') {
      onSyntheticMetricSelect('equity_multiplier')
    }
  }
  const isSelectedRatio = (id: string) => !selectedSyntheticMetricId && (selectedRatioId ? selectedRatioId === id : id === roe?.id)
  return (
    <div style={{ display: 'grid', gap: 'var(--s5)' }}>
      <section className="hero rise">
        <div className="hero-top">
          <div>
            <h1>DuPont drivers</h1>
            <p>
              DuPont explains return on equity as <strong>ROE = net margin x asset turnover x equity multiplier</strong>. Margin shows profit per sales dollar, asset turnover shows how productively assets create sales, and the equity multiplier separates leverage from operating performance.
            </p>
          </div>
        </div>
        {roe ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--s3)', marginTop: 'var(--s5)' }}>
            <RatioStat
              label="ROE"
              value={formatRatioValue(roe.latest, roe.unit)}
              accent={ratioTone(roe).color}
              selected={isSelectedRatio(roe.id)}
              onClick={() => onRatioSelect(roe.id)}
            />
            {roe.components.map((component) => (
              <RatioStat
                key={component.id}
                label={component.label}
                value={formatRatioValue(component.value, component.unit)}
                selected={selectedRatioId === component.id || selectedSyntheticMetricId === component.id}
                onClick={() => selectComponent(component)}
              />
            ))}
          </div>
        ) : (
          <div className="cfo-shell-notice">ROE drivers are not available for this period.</div>
        )}
      </section>
      {detailRatio || selectedDriverMetric ? (
        <div ref={detailRef}>
          {selectedDriverMetric ? (
            <SyntheticMetricCard metric={selectedDriverMetric} />
          ) : detailRatio ? (
            <RatioDetailCard
              ratio={detailRatio}
              ratioWindow={ratioWindow}
              onRevertAiChange={onRevertAiChange}
              onRedirectAiChange={onRedirectAiChange}
              aiChangeBusy={aiChangeBusy}
            />
          ) : null}
        </div>
      ) : null}
      {ccc ? (
        <section className="card rise" style={{ padding: 'var(--s5)' }}>
          <h3 style={{ margin: 0 }}>Cash conversion cycle</h3>
          <p style={{ color: 'var(--ink-2)' }}>
            CCC is not one of the three DuPont factors, but it is the operating-cash companion to ROE: it shows how long profits and supplier financing stay tied up before growth turns back into cash. CCC is calculated as <strong>{cccFormula}</strong>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <RatioStat
              label="CCC"
              value={formatRatioValue(ccc.latest, ccc.unit)}
              accent={ratioTone(ccc).color}
              selected={selectedRatioId === ccc.id}
              onClick={() => onRatioSelect(ccc.id)}
            />
            {ccc.components.map((component) => (
              <RatioStat
                key={component.id}
                label={component.label}
                value={formatRatioValue(component.value, component.unit)}
                selected={selectedRatioId === component.id}
                onClick={() => selectComponent(component)}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}

const sfgTrendPeriods = (growth: QuickBooksGrowthAnalysisResponse, asOf: string | null | undefined): SfgTrendPeriod[] => {
  const endPeriod = normalizeAsOfPeriod(asOf || growth.config?.asOfDate || growth.config?.source?.asOfDate || null)
  return (growth.trends?.periods || [])
    .filter((row) => row.result && Number.isFinite(row.result.annualSfgRate))
    .filter((row) => {
      if (!endPeriod) return true
      const period = normalizeAsOfPeriod(row.period)
      return !!period && period <= endPeriod
    })
    .sort((a, b) => normalizeAsOfPeriod(a.period).localeCompare(normalizeAsOfPeriod(b.period)))
    .slice(-12)
}

function SfgTrendGraph({ periods }: { periods: SfgTrendPeriod[] }) {
  const values = periods
    .map((point) => point.result?.annualSfgRate)
    .filter((value): value is number => value != null && Number.isFinite(value))
  if (periods.length < 2 || !values.length) {
    return <div className="cfo-shell-notice">Not enough SFG history for a trend graph yet.</div>
  }
  const min = Math.min(...values, 0)
  const max = Math.max(...values, 0)
  const pad = (max - min || Math.abs(max) || 1) * 0.16
  const yMin = min - pad
  const yMax = max + pad
  const width = 900
  const height = 340
  const left = 54
  const right = 44
  const top = 30
  const bottom = 44
  const plotW = width - left - right
  const plotH = height - top - bottom
  const toX = (idx: number) => left + (idx / Math.max(periods.length - 1, 1)) * plotW
  const toY = (value: number) => top + ((yMax - value) / (yMax - yMin || 1)) * plotH
  const linePoints = periods
    .map((point, idx) => `${toX(idx).toFixed(1)},${toY(point.result?.annualSfgRate || 0).toFixed(1)}`)
    .join(' ')
  const areaPath = `M ${toX(0).toFixed(1)},${toY(periods[0].result?.annualSfgRate || 0).toFixed(1)} ` +
    periods.slice(1).map((point, idx) => `L ${toX(idx + 1).toFixed(1)},${toY(point.result?.annualSfgRate || 0).toFixed(1)}`).join(' ') +
    ` L ${toX(periods.length - 1).toFixed(1)},${(top + plotH).toFixed(1)} L ${toX(0).toFixed(1)},${(top + plotH).toFixed(1)} Z`
  const zeroY = yMin <= 0 && yMax >= 0 ? toY(0) : null
  const first = periods[0]
  const last = periods[periods.length - 1]
  const firstValue = first.result?.annualSfgRate || 0
  const lastValue = last.result?.annualSfgRate || 0
  const labelY = (y: number) => Math.min(top + plotH - 7, Math.max(top + 14, y < top + plotH * 0.35 ? y + 22 : y - 13))

  return (
    <div className="fg-ratio-trend" style={{ maxWidth: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Self-financeable growth trend">
        <defs>
          <linearGradient id="fg-sfg-trend-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.2} />
            <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <line x1={left} y1={top + plotH} x2={left + plotW} y2={top + plotH} stroke="var(--grid)" strokeWidth={1.5} />
        {zeroY != null ? (
          <>
            <line x1={left} y1={zeroY} x2={left + plotW} y2={zeroY} stroke="var(--muted)" strokeWidth={1.2} strokeDasharray="5 5" opacity={0.65} />
            <text x={left - 8} y={zeroY + 4} textAnchor="end" style={{ font: '700 11px var(--font-ui)', fill: 'var(--muted)' }}>0%</text>
          </>
        ) : null}
        <path d={areaPath} fill="url(#fg-sfg-trend-grad)" />
        <polyline points={linePoints} fill="none" stroke="var(--brand)" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
        {periods.map((point, idx) => {
          const value = point.result?.annualSfgRate || 0
          const isEnd = idx === 0 || idx === periods.length - 1
          return (
            <g key={`${point.period}-${idx}`}>
              {isEnd ? <circle cx={toX(idx)} cy={toY(value)} r={7} fill="var(--paper)" /> : null}
              <circle cx={toX(idx)} cy={toY(value)} r={isEnd ? 5 : 3.6} fill="var(--brand)">
                <title>{`${formatAsOf(point.period || '')}: SFG ${formatPercent(value)}; net margin ${formatPercent(point.result?.netMarginPct)}; cash cycle ${formatRatioValue(point.result?.cashConversionCycleDays, 'days')}`}</title>
              </circle>
            </g>
          )
        })}
        <text x={toX(0) + 4} y={labelY(toY(firstValue))} textAnchor="start" style={{ font: '800 12.5px var(--font-ui)', fill: 'var(--brand)' }}>
          {formatPercent(firstValue)}
        </text>
        <text x={toX(periods.length - 1) - 4} y={labelY(toY(lastValue))} textAnchor="end" style={{ font: '800 12.5px var(--font-ui)', fill: 'var(--brand)' }}>
          {formatPercent(lastValue)}
        </text>
        {periods.map((point, idx) => (
          idx === 0 || idx === periods.length - 1 || idx % 3 === 0 ? (
            <text key={`sfg-tick-${point.period}-${idx}`} x={toX(idx)} y={height - 12} textAnchor={idx === 0 ? 'start' : idx === periods.length - 1 ? 'end' : 'middle'} style={{ font: '600 11.5px var(--font-ui)', fill: 'var(--muted)' }}>
              {formatShortMonth(point.period)}
            </text>
          ) : null
        ))}
      </svg>
    </div>
  )
}

function isSfgMetricId(id: SyntheticMetricId | null): id is SfgMetricId {
  return id === 'annual_sfg'
    || id === 'cash_generated_per_sales_dollar'
    || id === 'cash_required_per_sales_dollar'
    || id === 'cash_conversion_cycle'
}

function finiteNumber(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value)
}

function buildSfgMetric(id: SfgMetricId, base: GrowthScenarioResult | null): SyntheticMetricCardModel {
  const hasInventory = finiteNumber(base?.dio) && Math.abs(base.dio) > 0.000001
  const cashRequiredFormula = hasInventory
    ? 'COGS % x ((DSO + DIO - DPO) / (DSO + DIO)) + OpEx % x 50%'
    : 'COGS % x ((DSO - DPO) / DSO) + OpEx % x 50%'
  const cycleFormula = hasInventory ? 'DSO + DIO - DPO' : 'DSO - DPO'
  switch (id) {
    case 'cash_generated_per_sales_dollar':
      return {
        id,
        label: 'Cash Generated / Sales $',
        value: formatPercent(base?.cashGeneratedPerSalesDollar),
        description: 'The internal cash-generating profit left behind by each sales dollar.',
        formula: 'Profit / revenue',
        why: 'This is the generated-cash side of SFG. Higher values mean each new dollar of sales creates more internal cash to fund the next operating cycle.',
        source: 'Revenue and profit from the selected statement period.',
        components: [
          { label: 'Revenue', value: formatRatioValue(base?.revenue, 'currency') },
          { label: 'Profit', value: formatRatioValue(base?.profit, 'currency') },
        ],
      }
    case 'cash_required_per_sales_dollar':
      return {
        id,
        label: 'Cash Required / Sales $',
        value: formatPercent(base?.cashRequiredPerSalesDollar),
        description: 'Estimated cash that must be tied up to support each additional sales dollar.',
        formula: cashRequiredFormula,
        why: 'This is the funding-demand side of SFG. Lower values mean growth absorbs less cash before customer collections and supplier financing recycle it back.',
        source: hasInventory ? 'COGS %, OpEx %, DSO, DIO, and DPO.' : 'COGS %, OpEx %, DSO, and DPO.',
        components: [
          { label: 'CCC', value: formatRatioValue(base?.cashConversionCycleDays, 'days') },
          { label: 'DSO', value: formatRatioValue(base?.dso, 'days') },
          ...(hasInventory ? [{ label: 'DIO', value: formatRatioValue(base?.dio, 'days') }] : []),
          { label: 'DPO', value: formatRatioValue(base?.dpo, 'days') },
        ],
      }
    case 'cash_conversion_cycle':
      return {
        id,
        label: 'Cash Conversion Cycle',
        value: formatRatioValue(base?.cashConversionCycleDays, 'days'),
        description: 'Net days cash is tied up between paying for inputs and collecting from customers.',
        formula: cycleFormula,
        why: 'CCC does not multiply into DuPont ROE, but it determines how quickly operating profits can be reused to finance growth.',
        source: hasInventory ? 'A/R, inventory, A/P, revenue, and COGS.' : 'A/R, A/P, revenue, and COGS.',
        components: [
          { label: 'DSO', value: formatRatioValue(base?.dso, 'days') },
          ...(hasInventory ? [{ label: 'DIO', value: formatRatioValue(base?.dio, 'days') }] : []),
          { label: 'DPO', value: formatRatioValue(base?.dpo, 'days') },
        ],
      }
    case 'annual_sfg':
    default:
      return {
        id: 'annual_sfg',
        label: 'Annual SFG',
        value: formatPercent(base?.annualSfgRate),
        accent: 'var(--brand)',
        description: 'Self-financeable growth capacity: how much growth the business can fund internally if current economics hold.',
        formula: 'Cash generated per sales dollar / cash required per sales dollar x cycles per year',
        why: 'Annual SFG ties margin and working-capital speed together. Profit creates internal cash, cash required consumes it, and cycles per year show how many times the same cash can be reused.',
        source: hasInventory ? 'Profit, revenue, A/R, inventory, A/P, COGS, and operating expenses.' : 'Profit, revenue, A/R, A/P, COGS, and operating expenses.',
        components: [
          { label: 'Generated / sales $', value: formatPercent(base?.cashGeneratedPerSalesDollar) },
          { label: 'Required / sales $', value: formatPercent(base?.cashRequiredPerSalesDollar) },
          { label: 'Cycles / year', value: formatRatioValue(base?.cyclesPerYear, 'times') },
          { label: 'CCC', value: formatRatioValue(base?.cashConversionCycleDays, 'days') },
        ],
      }
  }
}

function RatioSfgPanel({
  ratios,
  growth,
  error,
  loading,
  asOf,
  ratioWindow,
  selectedRatioId,
  selectedSyntheticMetricId,
  onRatioSelect,
  onSyntheticMetricSelect,
  detailRef,
  onRevertAiChange,
  onRedirectAiChange,
  aiChangeBusy,
}: {
  ratios: RatioAnalysisResponse | null
  growth: QuickBooksGrowthAnalysisResponse | null
  error: string
  loading: boolean
  asOf?: string | null
  ratioWindow: RatioWindow
  selectedRatioId: string | null
  selectedSyntheticMetricId: SyntheticMetricId | null
  onRatioSelect: (id: string) => void
  onSyntheticMetricSelect: (id: SyntheticMetricId) => void
  detailRef: DetailRef
  onRevertAiChange?: (claimId: string, originId: string) => void
  onRedirectAiChange?: (originId: string, instruction: string) => void
  aiChangeBusy?: boolean
}) {
  if (error) return <div className="cfo-shell-notice error">{error}</div>
  if (!growth && loading) return <CfoCardSkeletons count={3} label="Loading SFG drivers…" />
  if (!growth) return <div className="cfo-shell-notice">No SFG drivers are available for this workbook yet.</div>
  const base = growth.baseline || null
  const trendPeriods = sfgTrendPeriods(growth, asOf)
  const latestTrend = trendPeriods[trendPeriods.length - 1]?.result || null
  const allRatios = ratios?.categories.flatMap((group) => group.ratios).filter((r) => r.ai_change?.kind !== 'demoted') || []
  const ratioById = new Map(allRatios.map((ratio) => [ratio.id, ratio]))
  const ccc = ratioById.get('cash_conversion_cycle') || null
  const selectedDetailRatio = selectedRatioId ? ratioById.get(selectedRatioId) || null : null
  const selectedSfgMetricId = isSfgMetricId(selectedSyntheticMetricId) ? selectedSyntheticMetricId : 'annual_sfg'
  const selectedSfgMetric = selectedDetailRatio ? null : buildSfgMetric(selectedSfgMetricId, base)
  const selectCashCycle = () => {
    if (ccc) {
      onRatioSelect(ccc.id)
    } else {
      onSyntheticMetricSelect('cash_conversion_cycle')
    }
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--s5)' }}>
      <section className="hero rise">
        <div className="hero-top">
          <div>
            <h1>Self-financeable growth</h1>
            <p>
              SFG estimates how much growth the business can fund internally: <strong>cash generated per sales dollar / cash required per sales dollar x cycles per year</strong>. Profitability supplies cash; the cash conversion cycle determines how quickly that cash can be reused.
            </p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--s3)', marginTop: 'var(--s5)' }}>
          <RatioStat
            label="Annual SFG"
            value={formatPercent(base?.annualSfgRate)}
            accent="var(--brand)"
            selected={!selectedDetailRatio && selectedSfgMetricId === 'annual_sfg'}
            onClick={() => onSyntheticMetricSelect('annual_sfg')}
          />
          <RatioStat
            label="Cash generated / sales $"
            value={formatPercent(base?.cashGeneratedPerSalesDollar)}
            selected={!selectedDetailRatio && selectedSfgMetricId === 'cash_generated_per_sales_dollar'}
            onClick={() => onSyntheticMetricSelect('cash_generated_per_sales_dollar')}
          />
          <RatioStat
            label="Cash required / sales $"
            value={formatPercent(base?.cashRequiredPerSalesDollar)}
            selected={!selectedDetailRatio && selectedSfgMetricId === 'cash_required_per_sales_dollar'}
            onClick={() => onSyntheticMetricSelect('cash_required_per_sales_dollar')}
          />
          <RatioStat
            label="Cash conversion cycle"
            value={formatRatioValue(base?.cashConversionCycleDays, 'days')}
            selected={selectedDetailRatio?.id === 'cash_conversion_cycle' || (!selectedDetailRatio && selectedSfgMetricId === 'cash_conversion_cycle')}
            onClick={selectCashCycle}
          />
        </div>
      </section>
      {selectedDetailRatio || selectedSfgMetric ? (
        <div ref={detailRef}>
          {selectedDetailRatio ? (
            <RatioDetailCard
              ratio={selectedDetailRatio}
              ratioWindow={ratioWindow}
              onRevertAiChange={onRevertAiChange}
              onRedirectAiChange={onRedirectAiChange}
              aiChangeBusy={aiChangeBusy}
            />
          ) : selectedSfgMetric ? (
            <SyntheticMetricCard metric={selectedSfgMetric} />
          ) : null}
        </div>
      ) : null}
      <section className="card rise" style={{ padding: 'var(--s5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--s4)', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 'var(--s3)' }}>
          <h3 style={{ margin: 0 }}>SFG trend</h3>
          {latestTrend ? (
            <span style={{ color: 'var(--muted)', fontWeight: 700 }}>
              Latest point · SFG {formatPercent(latestTrend.annualSfgRate)} · cash cycle {formatRatioValue(latestTrend.cashConversionCycleDays, 'days')}
            </span>
          ) : null}
        </div>
        <SfgTrendGraph periods={trendPeriods} />
      </section>
    </div>
  )
}

const formatPercent = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

export default CfoRatiosPage
